import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BannerAd = ({ platform = 'vod' }) => {
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const endpoint = platform === 'vod' 
          ? '/api/banner-ads/public/vod' 
          : '/api/banner-ads/public/live';
        const res = await fetch(`${API}${endpoint}`);
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setAds(data);
        }
      } catch (err) {
        console.error('Error fetching banner ads:', err);
      }
    };

    fetchAds();
  }, [platform]);

  // Rotate ads if multiple
  useEffect(() => {
    if (ads.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % ads.length);
      }, 10000); // Rotate every 10 seconds
      return () => clearInterval(interval);
    }
  }, [ads.length]);

  if (ads.length === 0) return null;

  const ad = ads[currentAdIndex];
  const imageUrl = isMobile && ad.mobile_image_url 
    ? (ad.mobile_image_url.startsWith('http') ? ad.mobile_image_url : `${API}${ad.mobile_image_url}`)
    : (ad.desktop_image_url?.startsWith('http') ? ad.desktop_image_url : `${API}${ad.desktop_image_url}`);

  if (!imageUrl) return null;

  const handleClick = () => {
    if (ad.click_url) {
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    }
  };

  const isFullWidth = ad.banner_style === 'full-width';

  return (
    <section className={`py-8 ${isFullWidth ? 'px-0' : 'px-4'}`}>
      <div className={isFullWidth ? 'w-full' : 'max-w-6xl mx-auto'}>
        {/* Sponsored Label */}
        {ad.show_sponsored_label && (
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Sponsored</span>
          </div>
        )}
        
        {/* Banner */}
        <div 
          className={`relative overflow-hidden cursor-pointer group ${
            isFullWidth ? 'w-full' : 'rounded-2xl'
          }`}
          onClick={handleClick}
        >
          {/* Image */}
          <div className={`relative ${isFullWidth ? 'aspect-[21/9] sm:aspect-[21/6]' : 'aspect-[21/9] sm:aspect-[21/7]'}`}>
            <img 
              src={imageUrl}
              alt={ad.alt_text || 'Advertisement'}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            
            {/* Gradient overlay for text */}
            {ad.has_text_overlay && (
              <div className={`absolute inset-0 bg-gradient-to-r ${
                ad.overlay_position === 'left' 
                  ? 'from-black/70 via-black/40 to-transparent' 
                  : ad.overlay_position === 'right'
                  ? 'from-transparent via-black/40 to-black/70'
                  : 'from-black/50 via-black/60 to-black/50'
              }`} />
            )}
            
            {/* Text Overlay */}
            {ad.has_text_overlay && (ad.overlay_headline || ad.overlay_subtext || ad.overlay_button_text) && (
              <div className={`absolute inset-0 flex flex-col justify-center p-6 sm:p-10 ${
                ad.overlay_position === 'left' 
                  ? 'items-start text-left' 
                  : ad.overlay_position === 'right'
                  ? 'items-end text-right'
                  : 'items-center text-center'
              }`}>
                {ad.overlay_headline && (
                  <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                    {ad.overlay_headline}
                  </h3>
                )}
                {ad.overlay_subtext && (
                  <p className="text-sm sm:text-lg text-gray-200 mb-4 max-w-xl drop-shadow">
                    {ad.overlay_subtext}
                  </p>
                )}
                {ad.overlay_button_text && (
                  <button className="px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-colors flex items-center gap-2">
                    {ad.overlay_button_text}
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            
            {/* Hover effect */}
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
          </div>
        </div>
        
        {/* Ad indicators if multiple */}
        {ads.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            {ads.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAdIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentAdIndex ? 'bg-purple-500' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BannerAd;
